import {useEffect, useState} from "react";
import {useDebounce} from "use-debounce";
import {API, APIv2, APIClient} from "../../api/api";

type ReturnType<T> = {
    data: T | null,
    reload: () => void,
}

function useData<T>(api: APIClient, method: string, _query?: Record<string, any>, transformer?: (rawData: unknown) => T, fallback?: T): ReturnType<T> {
    const [data, setData] = useState<T | null>(fallback ?? null);
    const [reload, setReload] = useState<number>(0);

    const [query] = useDebounce(_query, 250);
    const queryString = new URLSearchParams(query).toString();

    useEffect(() => {
        let cancelled = false;

        async function fetchData(): Promise<void> {
            const data = await api.get<unknown | T>(method, query ? '?' + queryString : '');
            if (!cancelled) {
                setData(transformer ? transformer(data) : (data as T));
            }
        }

        void fetchData();

        return () => {
            cancelled = true;
        };
    }, [method, queryString, reload])

    return {data, reload: () => setReload(reload + 1)};
}

export function useV1Data<T>(method: string, _query?: Record<string, any>, transformer?: (rawData: unknown) => T, fallback?: T): ReturnType<T> {
    return useData(API, method, _query, transformer, fallback);
}

export function useV2Data<T>(method: string, _query?: Record<string, any>, transformer?: (rawData: unknown) => T, fallback?: T): ReturnType<T> {
    return useData(APIv2, method, _query, transformer, fallback);
}
